<script setup lang="ts">
import {computed} from "vue";
import {ClipboardCheck, Hand, TriangleAlert, X} from "@lucide/vue";
import {Dialog, DialogContent, DialogDescription, DialogTitle} from "@/core/components/ui/dialog";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {Progress} from "@/core/components/ui/progress";
import MedicationCheckupRetainedTreatmentsList
    from "@/medication-checkup/components/MedicationCheckupRetainedTreatmentsList.vue";
import {TreatmentLine} from "@/medication-checkup/interfaces/treatment-selections";
import {useTreatmentSelectionsStore} from "@/medication-checkup/stores/treatment-selections-store";
import {pluralize} from "@/core/filters/Pluralize";

/**
 * The last look at the collection before it closes: the treatments
 * retained, each with its posology drawn, and those still without one.
 *
 * The closing freezes the record and nothing lets the pharmacist come back
 * to fill a posology in, so a missing one is pointed at — the pencil opens
 * the dialog over this one — but never blocks: some treatments have no
 * posology to give.
 */

defineProps<{
    open: boolean;
}>();

const emit = defineEmits<{
    close: [];
    validate: [];
    edit: [line: TreatmentLine];
}>();

const selection = useTreatmentSelectionsStore();

const entries = computed(() => selection.retained.map(line => ({line, posology: selection.posology(line)})));

const dosed = computed(() => entries.value.length - selection.missing.length);

const progress = computed(() => entries.value.length === 0 ? 0 : Math.round(dosed.value / entries.value.length * 100));

const warning = computed(() => {
    const missing = selection.missing.length;

    if (missing === 0) {
        return null;
    }

    return `${missing} ${pluralize('traitement', missing)} sans posologie. `
        + 'Après validation, il ne sera plus possible de les renseigner.';
});
</script>

<template>
  <Dialog :open="open" @update:open="value => !value && emit('close')">
    <DialogContent
      class="flex max-h-[85vh] max-w-[720px] flex-col gap-0 overflow-hidden rounded-[14px] border-[1.5px] border-solid border-primary p-0 shadow-deep"
      hide-close
      @open-auto-focus.prevent
    >
      <div class="flex flex-col gap-[7px] bg-gradient-to-r from-primary-dark to-primary px-3.5 pb-2.5 pt-3 text-white">
        <span class="flex items-center gap-[7px]">
          <ClipboardCheck class="size-[19px]" />
          <DialogTitle class="flex-1 text-[14px] font-bold !text-white">Traitements retenus</DialogTitle>
          <span class="text-[13px] font-bold">
            {{ dosed }} / {{ entries.length }} {{ pluralize('posologie', entries.length) }}
          </span>
          <button type="button" class="ml-1.5 !text-white" title="Fermer" @click="emit('close')">
            <X class="size-[19px]" />
          </button>
        </span>
        <DialogDescription class="sr-only">
          Les traitements retenus pour le bilan et leur posologie, avant de valider le recueil.
        </DialogDescription>
        <Progress :model-value="progress" class="h-[5px] bg-white/25 sm:h-[5px]" indicator-class="bg-secondary" />
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto">
        <div v-if="entries.length === 0" class="flex flex-col items-center gap-1.5 px-3.5 py-5 text-center">
          <Hand class="size-[26px] text-lightPurple" />
          <span class="text-[12px] leading-snug text-muted-foreground">
            Cochez les traitements que le patient prend : ils s'ajoutent ici, avec leur posologie.
          </span>
        </div>
        <MedicationCheckupRetainedTreatmentsList
          v-else
          :entries="entries"
          editable
          @edit="emit('edit', $event)"
        />
      </div>

      <div class="flex shrink-0 items-center justify-end gap-2.5 border-t border-solid border-gray-light px-3.5 py-3">
        <span v-if="warning" class="mr-auto flex items-start gap-1.5 text-[11px] font-semibold text-red-light">
          <TriangleAlert class="mt-px size-3.5 shrink-0" />
          {{ warning }}
        </span>
        <BaseButton variant="text" @click="emit('close')">
          Revenir à la sélection
        </BaseButton>
        <BaseButton primary :disabled="entries.length === 0" @click="emit('validate')">
          Valider les traitements
        </BaseButton>
      </div>
    </DialogContent>
  </Dialog>
</template>
